import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, Package, FileText, ReceiptEuro, Settings as SettingsIcon, Menu, X } from "lucide-react";
import { useI18n } from "@/i18n";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/", key: "dashboard", icon: LayoutDashboard, end: true },
  { to: "/customers", key: "customers", icon: Users },
  { to: "/products", key: "products", icon: Package },
  { to: "/quotes", key: "quotes", icon: FileText },
  { to: "/invoices", key: "invoices", icon: ReceiptEuro },
  { to: "/settings", key: "settings", icon: SettingsIcon },
];

const Layout = ({ children }) => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const SidebarContent = () => (
    <>
      <div className="flex items-center gap-3 px-6 py-7">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-primary-foreground">
          <ReceiptEuro className="h-5 w-5" strokeWidth={2} />
        </div>
        <div>
          <p className="font-display text-lg font-bold leading-none text-slate-900">Factugest</p>
          <p className="text-xs text-muted-foreground">{t("appName")}</p>
        </div>
      </div>
      <nav className="flex flex-1 flex-col gap-1 px-3">
        {NAV.map(({ to, key, icon: Icon, end }) => (
          <NavLink
            key={key}
            to={to}
            end={end}
            data-testid={`nav-${key}`}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              cn(
                "nav-link flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium",
                isActive
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-slate-600 hover:bg-accent hover:text-accent-foreground"
              )
            }
          >
            <Icon className="h-[18px] w-[18px]" strokeWidth={1.75} />
            {t(`nav.${key}`)}
          </NavLink>
        ))}
      </nav>
    </>
  );

  return (
    <div className="flex min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden w-64 shrink-0 flex-col border-r border-border bg-white lg:flex">
        <SidebarContent />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 flex h-full w-64 flex-col bg-white shadow-xl">
            <button className="absolute right-3 top-4 text-slate-500" onClick={() => setOpen(false)} data-testid="close-menu">
              <X className="h-5 w-5" />
            </button>
            <SidebarContent />
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-border bg-white/80 px-4 backdrop-blur-md lg:px-8">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="open-menu">
            <Menu className="h-6 w-6 text-slate-700" />
          </button>
          <div className="flex-1" />
        </header>
        <main className="flex-1 p-4 lg:p-8">{children}</main>
      </div>
    </div>
  );
};

export default Layout;
